/**
 * Add considerations for VBP
 */
import React, { useState } from "react";
import { UnitJourneyWrapper } from "@/components/journey/unitJourneyWrapper";
import { UnitJourneyContext } from "@/context/unitJourneyContext";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { UnitCheckout } from "@/components/journey/scenes/unitCheckout";
import type { SeVaporOptions, UnitGroup } from "@/types";
import { VbpModalContent } from "@glidevvr/se-components";
import { emitUnitEvent } from "@/utils/units/unitEvents";
import { useFacilitiesContext } from "@/hooks/useFacilitiesContext";
import { translateWithMultipleSources } from "@/lib/translate";
import "@glidevvr/se-components/style.css";
import { FunnelType } from "@/lib/utils";
import { UnitFunnel } from "@/components/journey/scenes/unitFunnel";

const shouldShowRentReserveStep = (
    unit: UnitGroup,
    settings: SeVaporOptions,
): boolean => {
    if (settings.buttonLayout === "combined") return true;
    return !(unit.sub_groups && unit.sub_groups.length > 1);
};

export const UnitAction = React.memo(() => {
    const options = useDisplayOptionContext();
    const unitContext = useUnitItemContext();
    const { facilities } = useFacilitiesContext();
    const { unitGroup, selectedPromo, setSelectedPromo } = unitContext;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [unitJourneyStep, setUnitJourneyStep] = useState<string>("");
    const [selectedUnitId, setSelectedUnitId] = useState<number | string>(
        unitGroup.representative_unit.id,
    );
    const [selectedFacility] = useState(facilities.find( facility => facility.id === unitGroup.representative_unit.facility_id));
    const [funnelType, setFunnelType] = useState<FunnelType | undefined>(undefined);

    const steps = React.useMemo(() => {
        const list: string[] = [];
        if (unitGroup?.sub_groups?.length > 1) {
            list.push("vbp");
        } else if (shouldShowRentReserveStep(unitGroup, options)) {
            list.push("rent-reserve-quote");
        }
        list.push("funnel");
        return list;
    }, [unitGroup.sub_groups, options]);

    const journeyContextValue = React.useMemo(
        () => ({isOpen,
        setIsOpen,
        unitJourneyStep,
        setUnitJourneyStep,
        selectedUnitId,
        setSelectedUnitId,
        stepList: steps,
        funnelType,
        setFunnelType,
    }), [isOpen, unitJourneyStep, selectedUnitId, steps, funnelType],
    );

    const onRentalClick = (unitId: number) => {
        const facilityId = unitGroup.representative_unit.facility_id;
        emitUnitEvent(unitId, facilityId, selectedPromo, "rental");


        if (options.rentalApp?.location === "external" && options.rentalApp.externalUrl) {
            const facilityInfo = facilities.find((facility) => facility.id === facilityId);
            if (facilityInfo && options.rentalApp.externalUrl) {
                window.location.href = translateWithMultipleSources(
                    options.rentalApp.externalUrl,
                    {
                        unitGroup,
                        facility: facilityInfo,
                        promo: { id: selectedPromo },
                    },
                    Number(unitId),
                    Number(selectedPromo),
                );
            }
        } else {
            if (!isOpen) {
                setIsOpen(true);
            }
            setSelectedUnitId(unitId);
            setUnitJourneyStep("funnel");
            setFunnelType(FunnelType.RENT);
        }
    }

    const onReservationClick = (unitId: number) => {
        const facilityId = unitGroup.representative_unit.facility_id;
        const facilityInfo = facilities.find(
            (facility) => facility.id === facilityId,
        );
        emitUnitEvent(
            unitId,
            unitGroup.representative_unit.facility_id,
            selectedPromo,
            "reservation",
        );

        if (
            options.reservationApp?.location === "external" &&
            options.reservationApp.externalUrl
        ) {
            if (facilityInfo && options.reservationApp?.externalUrl) {
                window.location.href = translateWithMultipleSources(
                    options.reservationApp.externalUrl,
                    {
                        unitGroup,
                        facility: facilityInfo,
                    },
                    Number(unitId),
                    Number(selectedPromo),
                );
            }
        } else {
            if (!isOpen) {
                setIsOpen(true);
            }
            setSelectedUnitId(unitId);
            setFunnelType(FunnelType.RESERVE);
            setUnitJourneyStep("funnel");
        }
    }


    const renderContent = () => {
        return (
            <div className="journey-content">
                {unitJourneyStep === "vbp" && (
                    <VbpModalContent
                        unitGroup={unitGroup}
                        selectedPromo={selectedPromo}
                        onPromoChange={(promoId: string) => setSelectedPromo(prev => (prev === Number(promoId) ? prev : Number(promoId)))}
                        disableRental={options.disableRental || false}
                        disableSoftReservation={options.disableSoftReservation || false}
                        disableReservation={options.disableReservation || selectedFacility?.settings?.hide_reservation_button_on_vbp_modal  || false}
                        vacancyNotice={options.vacancyNotice}
                        vacancyThreshold={options.vacancyThreshold}
                        onRentalClick={onRentalClick}
                        onQuoteClick={() => console.log('Quote Clicked')}
                        onReservationClick={onReservationClick}
                        removeCents={selectedFacility?.settings?.remove_cents_from_rate}
                    />
                )}
                {unitJourneyStep === "rent-reserve-quote" && <UnitCheckout />}
                {unitJourneyStep === "funnel" && (
                    <UnitFunnel type="reserve" selectedUnitId={selectedUnitId}/>
                )}
            </div>
        );
    };

    return (
        <UnitJourneyContext.Provider value={journeyContextValue}>
            <UnitJourneyWrapper>{renderContent()}</UnitJourneyWrapper>
        </UnitJourneyContext.Provider>
    );
});

UnitAction.displayName = "UnitAction";
